import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import InterviewSchedulingModal from '../InterviewSchedulingModal';
import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { resumeService } from '../../services/resumeService';
import { COLORS } from '../../config/colors';

interface Resume {
  resume_id: string;
  job_id: string;
  candidate_name: string;
  candidate_email: string;
  skills: string[];
  location: string;
  experience_years: number;
  resume_status: string;
  notes: string;
  phone: string;
  education: string | object[];
  panel_list: string[] | null;
  createdAt: string;
  updatedAt: string;
  score: number;
}

interface Panelist {
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  firstname: string | null;
  lastname: string | null;
  fullname: string;
}

interface PanelManagementViewProps {
  activeTab: number;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PanelManagementView: React.FC<PanelManagementViewProps> = ({
  activeTab,
  onError,
  onSuccess,
}) => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(false);
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [selectedResumeForInterview, setSelectedResumeForInterview] = useState<Resume | null>(null);

  const loadResumes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?page=1&size=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resumes: ${response.status}`);
      }

      const data = await response.json();
      setResumes(data.items || []);
    } catch (error) {
      console.error('Error loading resumes:', error);
      onError(
        `Failed to load resumes: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Load all panelists
  const loadPanelists = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?page=1&size=100&role=panelist`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch panelists: ${response.status}`);
      }

      const data = await response.json();
      setPanelists(data.users || []);
    } catch (error) {
      console.error('Error loading panelists:', error);
      onError(
        `Failed to load panelists: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const handleInterviewScheduling = async (
    resumeId: string,
    panelId: string,
    scheduleData: any
  ) => {
    if (!selectedResumeForInterview) return;

    try {
      // assignment with status update
      await resumeService.assignPanelToResumeWithStatus(resumeId, panelId, {
        interviewDateTime: scheduleData.interviewDateTime || null,
        duration: scheduleData.duration || 60,
        notes: scheduleData.notes || '',
        avatureFeedbackLink: scheduleData.avatureFeedbackLink || '',
        candidateName: scheduleData.candidateName,
        candidateEmail: scheduleData.candidateEmail,
        jobId: scheduleData.jobId,
        jobTitle: scheduleData.jobTitle,
        panelistName: scheduleData.panelistName,
        panelistEmail: scheduleData.panelistEmail,
      });

      const panelistName =
        panelists.find(p => p.email === panelId)?.fullname || scheduleData.panelistName;
      onSuccess(`Interview scheduled with ${panelistName} successfully!`);

      setInterviewModalOpen(false);
      setSelectedResumeForInterview(null);
      await loadResumes();
    } catch (error) {
      console.error('Error in panel assignment/interview scheduling:', error);
      onError(
        `Failed to schedule interview: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // const handleInterviewScheduling = async (
  //   resumeId: string,
  //   panelId: string,
  //   scheduleData: any
  // ) => {
  //   if (!selectedResumeForInterview) return;

  //   try {
  //     const token = localStorage.getItem('access_token');

  //     if (scheduleData.panelOnly) {
  //       const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.ASSIGN_PANEL}`, {
  //         method: 'PUT',
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //           accept: 'application/json',
  //         },
  //         body: JSON.stringify({
  //           panel_list: [panelId],
  //           resume_id: resumeId,
  //           interviewDateTime: scheduleData.interviewDateTime || null,
  //           duration: scheduleData.duration || 60,
  //           notes: scheduleData.notes || '',
  //           avatureFeedbackLink: scheduleData.avatureFeedbackLink || '',
  //           candidateName: scheduleData.candidateName,
  //           candidateEmail: scheduleData.candidateEmail,
  //           jobId: scheduleData.jobId,
  //           jobTitle: scheduleData.jobTitle,
  //           panelistName: scheduleData.panelistName,
  //           panelistEmail: scheduleData.panelistEmail,
  //         }),
  //       });

  //       if (!response.ok) {
  //         throw new Error(`Failed to assign panelist: ${response.status}`);
  //       }

  //       const panelistName = panelists.find(p => p.email === panelId)?.fullname || panelId;
  //       onSuccess(`Resume assigned to ${panelistName} successfully!`);
  //     } else {
  //       // assign the panel
  //       const assignResponse = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.ASSIGN_PANEL}`, {
  //         method: 'PUT',
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //           accept: 'application/json',
  //         },
  //         body: JSON.stringify({
  //           panel_list: [panelId],
  //           resume_id: resumeId,
  //         }),
  //       });

  //       if (!assignResponse.ok) {
  //         throw new Error(`Failed to assign panelist: ${assignResponse.status}`);
  //       }
  //       try {
  //         // Note: Interview scheduling endpoint is not defined in apiEndpoints.ts
  //         // Adding it as a direct endpoint since it's missing from the configuration
  //         const scheduleResponse = await fetch(`${BASE_URL}/interviews/schedule`, {
  //           method: 'POST',
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             'Content-Type': 'application/json',
  //             accept: 'application/json',
  //           },
  //           body: JSON.stringify({
  //             resume_id: resumeId,
  //             panelist_email: panelId,
  //             interview_datetime: scheduleData.interviewDateTime,
  //             duration: scheduleData.duration,
  //             notes: scheduleData.notes,
  //             candidate_name: scheduleData.candidateName,
  //             candidate_email: scheduleData.candidateEmail,
  //             job_id: scheduleData.jobId,
  //             job_title: scheduleData.jobTitle,
  //             panelist_name: scheduleData.panelistName,
  //           }),
  //         });

  //         if (scheduleResponse.ok) {
  //           onSuccess(
  //             `Resume assigned to ${scheduleData.panelistName} and interview scheduled successfully!`
  //           );
  //         } else {
  //           onSuccess(`Resume assigned to ${scheduleData.panelistName} successfully.`);
  //         }
  //       } catch (scheduleError) {
  //         console.warn('Interview scheduling failed:', scheduleError);
  //         onSuccess(`Resume assigned to ${scheduleData.panelistName} successfully.`);
  //       }
  //     }

  //     setInterviewModalOpen(false);
  //     setSelectedResumeForInterview(null);
  //     await loadResumes();
  //   } catch (error) {
  //     console.error('Error in panel assignment/scheduling:', error);
  //     onError(
  //       `Failed to assign resume: ${error instanceof Error ? error.message : 'Unknown error'}`
  //     );
  //   }
  // };

  // Handle assign button click
  const handleAssignButtonClick = (resume: Resume) => {
    setSelectedResumeForInterview(resume);
    setInterviewModalOpen(true);
  };

  useEffect(() => {
    loadResumes();
    loadPanelists();
  }, []);

  // Group resumes by panelist
  const groupResumesByPanelist = () => {
    const groups: { [key: string]: Resume[] } = {};

    // Initialize groups for all panelists
    panelists.forEach(panelist => {
      groups[panelist.email] = [];
    });

    // Group resumes by their assigned panelists
    resumes.forEach(resume => {
      if (resume.panel_list && resume.panel_list.length > 0) {
        resume.panel_list.forEach(panelistEmail => {
          if (!groups[panelistEmail]) {
            groups[panelistEmail] = [];
          }
          groups[panelistEmail].push(resume);
        });
      }
    });

    return groups;
  };

  // Get unassigned resumes
  const getUnassignedResumes = () => {
    return resumes.filter(resume => !resume.panel_list || resume.panel_list.length === 0);
  };

  const convertResumeForModal = (resume: Resume) => {
    return {
      id: resume.resume_id,
      candidateName: resume.candidate_name,
      email: resume.candidate_email,
      jobId: resume.job_id,
      jobTitle: `Job ${resume.job_id}`,
    };
  };

  const convertPanelistsForModal = () => {
    return panelists.map(panelist => ({
      id: panelist.email,
      name: panelist.fullname || panelist.username,
      email: panelist.email,
      role: panelist.role || 'Panelist',
    }));
  };

  const renderPanelManagement = () => {
    const groupedResumes = groupResumesByPanelist();
    const unassignedResumes = getUnassignedResumes();

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                  {panelists.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Panelists
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                  {resumes.length - unassignedResumes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned Resumes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                  {unassignedResumes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unassigned Resumes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                  {resumes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Resumes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: COLORS.PRIMARY_BLUE_DARK,
            }}
          >
            <PersonIcon />
            Panelist Assignments
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadResumes();
              loadPanelists();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Panel Assignments */}
            <Grid container spacing={3}>
              {/* Assigned Panels */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    {panelists.map(panelist => {
                      const assignedResumes = groupedResumes[panelist.email] || [];

                      return (
                        <Accordion key={panelist.email} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}
                            >
                              <Avatar sx={{ bgcolor: COLORS.PRIMARY_BLUE_DARK, width: 32, height: 32 }}>
                                {panelist.username.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {panelist.fullname || panelist.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {panelist.email}
                                </Typography>
                              </Box>
                              <Chip
                                label={`${assignedResumes.length} Resume${assignedResumes.length !== 1 ? 's' : ''}`}
                                color={assignedResumes.length > 0 ? 'primary' : 'default'}
                                size="small"
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {assignedResumes.length === 0 ? (
                              <Alert severity="info">
                                No resumes assigned to this panelist yet.
                              </Alert>
                            ) : (
                              <List>
                                {assignedResumes.map(resume => (
                                  <ListItem key={resume.resume_id} divider>
                                    <ListItemAvatar>
                                      <Avatar sx={{ bgcolor: '#f0f9ff', color:COLORS.PRIMARY_BLUE_DARK }}>
                                        {resume.candidate_name.charAt(0).toUpperCase()}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={resume.candidate_name}
                                      secondary={
                                        <Box>
                                          <Typography variant="caption" component="div">
                                            Job: {resume.job_id} • Score: {resume.score}%
                                          </Typography>
                                          <Typography variant="caption" component="div">
                                            {resume.candidate_email}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    <Chip
                                      label={resume.resume_status || 'No Status'}
                                      size="small"
                                      color={resume.resume_status ? 'primary' : 'default'}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>

              {/* Unassigned Resumes */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: COLORS.PRIMARY_BLUE_DARK,
                      }}
                    >
                      <AssignmentIcon />
                      Unassigned Resumes ({unassignedResumes.length})
                    </Typography>

                    {unassignedResumes.length === 0 ? (
                      <Alert severity="success">All resumes have been assigned to panelists!</Alert>
                    ) : (
                      <List>
                        {unassignedResumes.map(resume => (
                          <ListItem key={resume.resume_id} divider>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: COLORS.WARNING_LIGHT, color: COLORS.SUCCESS_DARK }}>
                                {resume.candidate_name.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={resume.candidate_name}
                              secondary={
                                <Box>
                                  <Typography variant="caption" component="div">
                                    Job: {resume.job_id} • Score: {resume.score}%
                                  </Typography>
                                  {/*Use new button handler */}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAssignButtonClick(resume)}
                                    sx={{ mt: 1 }}
                                  >
                                    Assign Panel
                                  </Button>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        <InterviewSchedulingModal
          open={interviewModalOpen}
          onClose={() => {
            setInterviewModalOpen(false);
            setSelectedResumeForInterview(null);
          }}
          resume={
            selectedResumeForInterview ? convertResumeForModal(selectedResumeForInterview) : null
          }
          panels={convertPanelistsForModal()}
          onSchedule={handleInterviewScheduling}
          loading={loading}
        />
      </Box>
    );
  };

  const renderAllPanelists = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, color: COLORS.PRIMARY_BLUE_DARK }}>
          All Panelists
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: COLORS.PRIMARY_BLUE_DARK }}>Panelist</TableCell>
              <TableCell sx={{ color: COLORS.PRIMARY_BLUE_DARK }}>Email</TableCell>
              <TableCell sx={{ color: COLORS.PRIMARY_BLUE_DARK }}>Status</TableCell>
              <TableCell sx={{ color: COLORS.PRIMARY_BLUE_DARK }}>Assigned Resumes</TableCell>
              <TableCell sx={{ color: COLORS.PRIMARY_BLUE_DARK }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {panelists.map(panelist => {
              const assignedCount = resumes.filter(
                resume => resume.panel_list && resume.panel_list.includes(panelist.email)
              ).length;

              return (
                <TableRow key={panelist.email}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: COLORS.PRIMARY_BLUE_DARK, width: 32, height: 32 }}>
                        {panelist.username.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {panelist.fullname || panelist.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {panelist.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{panelist.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={panelist.isActive ? 'Active' : 'Inactive'}
                      color={panelist.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${assignedCount} Resume${assignedCount !== 1 ? 's' : ''}`}
                      color={assignedCount > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dayjs(panelist.createdAt).format('MMM DD, YYYY')}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {activeTab === 0 && renderPanelManagement()}
      {activeTab === 1 && renderAllPanelists()}
    </Box>
  );
};

export default PanelManagementView;
